import { useState, useRef, useCallback } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  Eye,
  Edit3,
  Columns,
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  Link,
  List,
  ListOrdered,
  Image,
  Quote,
  Code,
  Minus,
  Video,
} from 'lucide-react';
import { MarkdownImageDialog } from './MarkdownImageDialog';
import { MarkdownVideoDialog } from './MarkdownVideoDialog';

type ViewMode = 'write' | 'preview' | 'split';

interface MarkdownPreviewProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  minHeight?: string;
  storageBucket?: string;
  storageFolder?: string;
}

interface ToolbarButton {
  icon: React.ReactNode;
  label: string;
  action: () => void;
}

export function MarkdownPreview({
  value,
  onChange,
  placeholder = 'Write your content here using Markdown...',
  error,
  minHeight = '400px',
  storageBucket = 'docs',
  storageFolder = 'images',
}: MarkdownPreviewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('write');
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
  const charCount = value.length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const insertText = useCallback((before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const textToInsert = selectedText || placeholder;
    
    const newValue = 
      value.substring(0, start) + 
      before + textToInsert + after + 
      value.substring(end);
    
    onChange(newValue);

    // Set cursor position after insertion
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.setSelectionRange(
        selectedText ? newCursorPos + after.length : start + before.length,
        selectedText ? newCursorPos + after.length : start + before.length + placeholder.length
      );
    }, 0);
  }, [value, onChange]);

  const insertAtLineStart = useCallback((prefix: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    
    const newValue = 
      value.substring(0, lineStart) + 
      prefix + 
      value.substring(lineStart);
    
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [value, onChange]);

  const insertAtCursor = useCallback((content: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      // Fallback: append to end
      onChange(value + '\n' + content + '\n');
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Add newlines if not at start of line
    const beforeCursor = value.substring(0, start);
    const afterCursor = value.substring(end);
    const needsNewlineBefore = beforeCursor.length > 0 && !beforeCursor.endsWith('\n');
    const needsNewlineAfter = afterCursor.length > 0 && !afterCursor.startsWith('\n');
    
    const newValue = 
      beforeCursor + 
      (needsNewlineBefore ? '\n' : '') +
      content +
      (needsNewlineAfter ? '\n' : '') +
      afterCursor;
    
    onChange(newValue);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + (needsNewlineBefore ? 1 : 0) + content.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [value, onChange]);

  const handleImageInsert = useCallback((markdown: string) => {
    insertAtCursor(markdown);
  }, [insertAtCursor]);

  const handleVideoInsert = useCallback((embedHtml: string) => {
    insertAtCursor(embedHtml);
  }, [insertAtCursor]);

  const toolbarButtons: (ToolbarButton | 'separator')[] = [
    {
      icon: <Bold className="h-4 w-4" />,
      label: 'Bold (Ctrl+B)',
      action: () => insertText('**', '**', 'bold text'),
    },
    {
      icon: <Italic className="h-4 w-4" />,
      label: 'Italic (Ctrl+I)',
      action: () => insertText('*', '*', 'italic text'),
    },
    'separator',
    {
      icon: <Heading1 className="h-4 w-4" />,
      label: 'Heading 1',
      action: () => insertAtLineStart('# '),
    },
    {
      icon: <Heading2 className="h-4 w-4" />,
      label: 'Heading 2',
      action: () => insertAtLineStart('## '),
    },
    {
      icon: <Heading3 className="h-4 w-4" />,
      label: 'Heading 3',
      action: () => insertAtLineStart('### '),
    },
    'separator',
    {
      icon: <List className="h-4 w-4" />,
      label: 'Bullet List',
      action: () => insertAtLineStart('- '),
    },
    {
      icon: <ListOrdered className="h-4 w-4" />,
      label: 'Numbered List',
      action: () => insertAtLineStart('1. '),
    },
    {
      icon: <Quote className="h-4 w-4" />,
      label: 'Quote',
      action: () => insertAtLineStart('> '),
    },
    'separator',
    {
      icon: <Link className="h-4 w-4" />,
      label: 'Link',
      action: () => insertText('[', '](url)', 'link text'),
    },
    {
      icon: <Image className="h-4 w-4" />,
      label: 'Insert Image',
      action: () => setImageDialogOpen(true),
    },
    {
      icon: <Video className="h-4 w-4" />,
      label: 'Embed Video',
      action: () => setVideoDialogOpen(true),
    },
    {
      icon: <Code className="h-4 w-4" />,
      label: 'Code',
      action: () => insertText('`', '`', 'code'),
    },
    {
      icon: <Minus className="h-4 w-4" />,
      label: 'Horizontal Rule',
      action: () => insertText('\n---\n', '', ''),
    },
  ];

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          insertText('**', '**', 'bold text');
          break;
        case 'i':
          e.preventDefault();
          insertText('*', '*', 'italic text');
          break;
        case 'k':
          e.preventDefault();
          insertText('[', '](url)', 'link text');
          break;
      }
    }
  }, [insertText]);

  return (
    <div className="space-y-2">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Formatting Buttons */}
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center gap-0.5 p-1 bg-muted rounded-md flex-wrap">
            {toolbarButtons.map((item, index) => {
              if (item === 'separator') {
                return <Separator key={index} orientation="vertical" className="h-6 mx-1" />;
              }
              return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={item.action}
                      className="h-7 w-7 p-0"
                      disabled={viewMode === 'preview'}
                    >
                      {item.icon}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">
                    {item.label}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </TooltipProvider>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <div className="text-xs text-muted-foreground hidden sm:block">
            {wordCount} words • ~{readingTime} min
          </div>
          <div className="flex items-center gap-0.5 p-1 bg-muted rounded-md">
            <Button
              type="button"
              variant={viewMode === 'write' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('write')}
              className="h-7 gap-1.5"
            >
              <Edit3 className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Write</span>
            </Button>
            <Button
              type="button"
              variant={viewMode === 'split' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('split')}
              className="h-7 gap-1.5"
            >
              <Columns className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Split</span>
            </Button>
            <Button
              type="button"
              variant={viewMode === 'preview' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('preview')}
              className="h-7 gap-1.5"
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Preview</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div
        className={cn(
          'grid gap-4 rounded-lg border',
          viewMode === 'split' ? 'grid-cols-2' : 'grid-cols-1',
          error && 'border-destructive'
        )}
        style={{ minHeight }}
      >
        {/* Write Panel */}
        {(viewMode === 'write' || viewMode === 'split') && (
          <div className={cn(viewMode === 'split' && 'border-r')}>
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="border-0 resize-none h-full min-h-[400px] font-mono text-sm rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ minHeight }}
            />
          </div>
        )}

        {/* Preview Panel */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div
            className={cn(
              'p-4 overflow-auto',
              viewMode === 'preview' && 'rounded-lg'
            )}
            style={{ minHeight }}
          >
            {value ? (
              <MarkdownRenderer content={value} compact />
            ) : (
              <p className="text-muted-foreground italic">
                Start writing to see a preview...
              </p>
            )}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Dialogs */}
      <MarkdownImageDialog
        open={imageDialogOpen}
        onOpenChange={setImageDialogOpen}
        onInsert={handleImageInsert}
        storageBucket={storageBucket}
        storageFolder={storageFolder}
      />
      <MarkdownVideoDialog
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
        onInsert={handleVideoInsert}
      />
    </div>
  );
}
